import React, {InputHTMLAttributes} from 'react';
import clsx from 'clsx';
import {ErrorType} from '@chekin/core';
import {TooltipInfo} from '@chekin/guestapp-ui-kit';
import {withEmptyDetection} from '../../hocs';
import redXIcon from '../../assets/icons/credit-card-red-x.svg?no-inline';
import {Fieldset} from '../Fieldset';
import {
  RelativeWrapper,
  StyledInput,
  WrapperInput,
  StatusIcon,
  InputErrorMessage,
  StatusCustomIcon,
  ContentStatusIcon,
} from './styled';

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  invalid?: boolean;
  error?: ErrorType;
  inputMode?:
    | 'text'
    | 'none'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal'
    | 'search';
  tooltipContent?: string;
  isMobileMode?: boolean;
  showStatusIcon?: boolean;
  empty?: boolean;
  onReset?: () => void;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      empty,
      onChange,
      className,
      label,
      disabled,
      name,
      error,
      children,
      tooltipContent,
      autoComplete = 'on',
      onBlur,
      onFocus,
      isMobileMode = true,
      showStatusIcon,
      onReset,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const touchedRef = React.useRef(false);
    const isCompleted = !empty && touchedRef.current;
    const isInvalid = !!error;

    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const handleLabelClick = React.useCallback(() => {
      inputRef.current?.focus();
      setIsFocused(true);
    }, []);

    const handleFocus = React.useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        onFocus?.(event);
        setIsFocused(true);
      },
      [onFocus],
    );

    const handleBlur = React.useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        onBlur?.(event);
        touchedRef.current = true;
        setIsFocused(false);
      },
      [onBlur],
    );

    return (
      <WrapperInput
        disabled={disabled}
        className={clsx('input-wrapper', className, {
          'input-wrapper--focused': isFocused,
          'input-wrapper--invalid': isInvalid,
          'input-wrapper--disabled': disabled,
          'input-wrapper--empty': empty,
        })}
      >
        <RelativeWrapper className="input">
          {children || (
            <StyledInput
              className={clsx(
                'input__input',
                'kiosko:!h-[106px] kiosko:p-7 kiosko:text-kiosko-s',
                {
                  'input--focused': isFocused,
                  'input--invalid': isInvalid,
                  'input--disabled': disabled,
                  'input--empty': empty,
                },
              )}
              ref={inputRef}
              data-testid="input"
              $isFocused={isFocused}
              onChange={onChange}
              disabled={disabled}
              aria-label={name}
              name={name}
              $invalid={isInvalid}
              $isEmpty={empty}
              placeholder={''}
              onFocus={handleFocus}
              onBlur={handleBlur}
              autoComplete={autoComplete}
              {...props}
            />
          )}
          {Boolean(error && showStatusIcon) && (
            <StatusIcon
              src={redXIcon}
              alt=""
              width={24}
              height={24}
              error={!!error}
              className={'input__status-icon'}
              onClick={() => {
                if (error && onReset) onReset();
              }}
            />
          )}
          {Boolean(isCompleted && showStatusIcon) && (
            <ContentStatusIcon>
              <StatusCustomIcon
                name="blueTickIcon"
                size={24}
                className={'input__status-icon'}
              />
            </ContentStatusIcon>
          )}
          <Fieldset
            legend={label}
            onClick={handleLabelClick}
            invalid={!!error}
            isFocused={isFocused}
            isEmpty={empty}
            isActivated={!empty || isFocused}
            label={label}
          />
          {tooltipContent && (
            <TooltipInfo
              className="absolute -right-6 top-1/2 -translate-y-1/2"
              side={isMobileMode ? 'top' : 'right'}
              content={tooltipContent}
            />
          )}
        </RelativeWrapper>

        {error && <InputErrorMessage>{error as string}</InputErrorMessage>}
      </WrapperInput>
    );
  },
);

const InputController = withEmptyDetection<HTMLInputElement, InputProps>(
  React.memo(Input),
);
export {Input, InputController};
